// Clone a vault to a device — what `cairn vault clone` does and the header
// Clone button. Sourced from specs/cli.md §5 (clone/link semantics).

import { IconCopy, IconFolders, IconTerminal2 } from "@tabler/icons-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CommandLine } from "@/components/command-line";
import { DocSection, K, P, Points } from "../doc-ui";

export function CloneVaultDoc() {
  return (
    <>
      <P>
        Cloning brings a vault that already exists on the server onto a machine: it prepares a
        local folder, and the first open in Obsidian pulls the content. You need the CLI installed
        and signed in first (see <em>Install &amp; update the CLI</em>).
      </P>

      <DocSection icon={IconTerminal2} title="Clone with the CLI">
        <CommandLine command="cairn vault list" comment="see the vaults you can access" />
        <CommandLine command="cairn vault clone <vault> [path]" />
        <Points>
          <li>
            <K>&lt;vault&gt;</K> is the vault's name or id. Names work when they're unique; the id
            always works.
          </li>
          <li>
            Omit <K>[path]</K> and the folder is named after the vault (so Obsidian's vault
            switcher shows the right name). An explicit path is used verbatim.
          </li>
        </Points>
      </DocSection>

      <DocSection
        icon={IconCopy}
        title="The Clone button"
        description="When viewing a vault here, the header has a Clone button."
      >
        <P>
          It copies the exact clone command for the vault you're looking at — with the vault id
          filled in — so you can paste it straight into a terminal on the machine you're setting
          up.
        </P>
      </DocSection>

      <DocSection icon={IconFolders} title="What clone actually does">
        <Points>
          <li>Creates the folder and registers it in Obsidian's vault switcher.</li>
          <li>
            Installs the Cairn plugin into the folder from your server (checksum-verified) and
            binds it to the remote vault — nothing to install inside Obsidian.
          </li>
          <li>
            Content appears on first open: clone prepares, Obsidian pulls. Open the folder as a
            vault in Obsidian and the plugin runs the first sync.
          </li>
        </Points>
        <Alert>
          <IconFolders />
          <AlertTitle>Cloning to a phone?</AlertTitle>
          <AlertDescription>
            There's no CLI on mobile — pair the phone and pick the vault in the plugin instead.
            See <em>Set up a phone or tablet</em>.
          </AlertDescription>
        </Alert>
      </DocSection>
    </>
  );
}
